<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import type { Account } from '@/types'
import { useDeleteLinkedAccountModal } from '@/composables/useDeleteLinkedAccountModal'

withDefaults(
  defineProps<{
    embedded?: boolean
  }>(),
  {
    embedded: false,
  }
)

const store = useStore()
const { openDeleteLinkedAccountModal } = useDeleteLinkedAccountModal()

const fidelAccounts = computed(() => store.state.accounts.fidelAccounts as Account[])
const cardAccounts = computed(() => store.state.accounts.cardAccounts as Account[])
const parAccounts = computed(() => store.state.accounts.parAccounts as Account[])

const matchingCards = computed(() => [
  ...fidelAccounts.value,
  ...cardAccounts.value,
  ...parAccounts.value,
])

const removeCard = (account: Account) => {
  store.dispatch('accounts/setAccountToBeDeleted', account.id)
  openDeleteLinkedAccountModal()
}

const noCardAccounts = computed(
  () =>
    !fidelAccounts.value.length &&
    !cardAccounts.value.length &&
    !parAccounts.value.length
)

const cardBrandLabel = (account: Account): string => {
  const css = account.cssClass ?? ''
  if (css.includes('scheme-mastercard')) return 'MC'
  if (css.includes('scheme-visa')) return 'VISA'
  if (css.includes('scheme-amex')) return 'AMEX'
  return 'CARD'
}

const isExpired = (account: Account): boolean =>
  (account.cssClass ?? '').includes('expired') || account.expiryDate === 'Expired'
</script>

<template>
  <component
    :is="embedded ? 'div' : 'section'"
    :class="embedded ? 'sa-matching-cards-panel' : 'sa-acct-section sa-matching-cards'"
    :aria-labelledby="embedded ? undefined : 'matching-cards-heading'"
  >
    <h2 v-if="!embedded" id="matching-cards-heading">
      {{ $t('Dashboard.Status.Cards.Heading') }}
    </h2>

    <div class="sa-matching-cards__notice" role="note">
      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          fill-rule="evenodd"
          d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.669-8 11.317C5.34 16.67 2 12.225 2 7c0-.68.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd"
        />
      </svg>
      <p>
        <strong>{{ $t('Dashboard.Status.Cards.NoticeBold') }}</strong>
        {{ $t('Dashboard.Status.Cards.NoticeRest') }}
      </p>
    </div>

    <div v-if="noCardAccounts" class="sa-matching-cards__empty">
      <p>{{ $t('Dashboard.Status.Cards.Empty') }}</p>
    </div>

    <div
      v-for="account in matchingCards"
      :key="account.id"
      class="sa-card-chip sa-card-chip--card"
    >
      <div
        class="sa-card-brand"
        :class="{ 'sa-card-brand--mc': cardBrandLabel(account) === 'MC' }"
        aria-hidden="true"
      >
        {{ cardBrandLabel(account) }}
      </div>
      <div class="sa-card-chip__body sa-card-chip__body--grow">
        <div class="sa-card-number">•••• •••• •••• {{ account.last4 }}</div>
        <div
          v-if="account.expiryDate && account.expiryDate !== 'Expired'"
          class="sa-card-chip__sub"
        >
          {{ $t('Dashboard.Status.Cards.Expires', { date: account.expiryDate }) }}
        </div>
      </div>
      <span v-if="isExpired(account)" class="sa-tag-expired">
        {{ $t('Dashboard.Status.Cards.Expired') }}
      </span>
      <span v-else class="sa-tag-active">{{ $t('Dashboard.Status.Cards.Active') }}</span>
      <button
        type="button"
        class="sa-btn-remove"
        :aria-label="
          $t('Dashboard.Status.Cards.RemoveAria', {
            brand: cardBrandLabel(account),
            last4: account.last4,
          })
        "
        @click="removeCard(account)"
      >
        {{ $t('Dashboard.Status.Cards.Remove') }}
      </button>
    </div>

    <p class="sa-matching-cards__footnote">{{ $t('Dashboard.Status.Cards.Footnote') }}</p>
  </component>
</template>
